"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type VideoHTMLAttributes,
} from "react";

type LazyAmbientVideoProps = {
  src: string;
  label: string;
  className?: string;
};

const LOAD_ROOT_MARGIN = "320px 0px";
const PLAY_THRESHOLD = 0.24;
const EXIT_THRESHOLD = 0.05;

export default function LazyAmbientVideo({
  src,
  label,
  className,
}: LazyAmbientVideoProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const visibleRef = useRef(false);
  const completedThisEntryRef = useRef(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [reducedMotion, setReducedMotion] = useState<boolean | null>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  const pauseVideo = useCallback(() => {
    videoRef.current?.pause();
  }, []);

  const playVideo = useCallback(() => {
    const video = videoRef.current;
    if (
      !video ||
      reducedMotion !== false ||
      videoFailed ||
      document.visibilityState === "hidden"
    ) {
      return;
    }
    if (!video.querySelector("source")) return;
    if (completedThisEntryRef.current || !video.paused) return;

    const playResult = video.play();
    playResult?.catch(() => {
      pauseVideo();
    });
  }, [pauseVideo, reducedMotion, videoFailed]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncPreference = () => setReducedMotion(mediaQuery.matches);
    syncPreference();
    mediaQuery.addEventListener?.("change", syncPreference);
    return () => mediaQuery.removeEventListener?.("change", syncPreference);
  }, []);

  useEffect(() => {
    if (reducedMotion !== true) return;
    pauseVideo();
  }, [pauseVideo, reducedMotion]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || reducedMotion !== false || videoFailed) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      visibleRef.current = true;
      const timeoutId = setTimeout(() => {
        setShouldLoad(true);
        playVideo();
      }, 0);
      return () => clearTimeout(timeoutId);
    }

    const loadObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setShouldLoad(true);
      },
      {
        rootMargin: LOAD_ROOT_MARGIN,
        threshold: 0,
      }
    );

    const playbackObserver = new IntersectionObserver(
      ([entry]) => {
        const ratio = entry?.intersectionRatio || 0;
        if (entry?.isIntersecting && ratio >= PLAY_THRESHOLD) {
          visibleRef.current = true;
          playVideo();
          return;
        }

        if (!entry?.isIntersecting || ratio <= EXIT_THRESHOLD) {
          visibleRef.current = false;
          pauseVideo();
          const video = videoRef.current;
          completedThisEntryRef.current = false;
          if (video && video.currentTime > 0) video.currentTime = 0;
        }
      },
      {
        threshold: [0, EXIT_THRESHOLD, PLAY_THRESHOLD, 0.6],
      }
    );

    loadObserver.observe(frame);
    playbackObserver.observe(frame);
    return () => {
      loadObserver.disconnect();
      playbackObserver.disconnect();
    };
  }, [pauseVideo, playVideo, reducedMotion, videoFailed]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        pauseVideo();
      } else if (visibleRef.current) {
        playVideo();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [pauseVideo, playVideo]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoad || reducedMotion !== false || videoFailed) return;
    video.load();
  }, [reducedMotion, shouldLoad, src, videoFailed]);

  const videoProps: VideoHTMLAttributes<HTMLVideoElement> = {
    autoPlay: false,
    controls: false,
    loop: false,
    muted: true,
    playsInline: true,
    preload: "metadata",
  };

  return (
    <div
      ref={frameRef}
      className={className}
      data-testid={`ambient-video-${label}`}
      data-video-ready={videoReady ? "true" : "false"}
      data-video-error={videoFailed ? "true" : "false"}
      data-motion-reduced={
        reducedMotion === null ? "unknown" : reducedMotion ? "true" : "false"
      }
      data-video-loaded={shouldLoad ? "true" : "false"}
      data-playback="once-per-entry"
    >
      <video
        ref={videoRef}
        {...videoProps}
        aria-hidden="true"
        tabIndex={-1}
        onCanPlay={() => {
          setVideoReady(true);
          if (visibleRef.current) playVideo();
        }}
        onPlay={() => {
          if (!visibleRef.current || reducedMotion !== false) pauseVideo();
        }}
        onError={() => {
          setVideoFailed(true);
          setVideoReady(false);
          pauseVideo();
        }}
        onEnded={() => {
          completedThisEntryRef.current = true;
          pauseVideo();
        }}
      >
        {shouldLoad && reducedMotion === false && !videoFailed ? (
          <source src={src} type="video/mp4" onError={() => setVideoFailed(true)} />
        ) : null}
      </video>
      <div aria-hidden="true" data-video-fallback="true" />
    </div>
  );
}
